<template>
  <div class="relative" ref="selectRef">
    <!-- Кнопка селекта -->
    <div class="bg-[#3176FF14] flex items-center rounded-lg overflow-hidden h-[30px]">
      <!-- Левая часть: название поля и стрелка направления -->
      <button
        class="flex items-center gap-1 py-0.5 px-2 whitespace-nowrap"
        :class="{ 'text-primary': sortedBy !== null && orderBy !== null }"
        @click="handleSortToggle"
      >
        {{ currentFieldTitle }}
        <img
          v-if="sortedBy !== null && orderBy !== null"
          src="@/public/assets/img/svg/ArrowUp.svg"
          alt="sort direction"
          class="transition-transform duration-300"
          :class="{ 'rotate-180': orderBy === 'asc' }"
        />
      </button>
      
      <!-- Разделитель -->
      <!-- <div class="w-px h-6 bg-[#3176FF40]"></div> -->
      
      <!-- Правая часть: стрелка-кнопка для открытия селекта -->
      <button
        v-if="sortedFields.length > 1"
        class="flex items-center justify-center py-0.5 px-2"
        @click="toggleDropdown"
      >
        <IChevronDown
          class="transition-transform duration-300 w-4"
          :class="{ 'rotate-180': !isDropdownOpen }"
        />
      </button>
    </div>

    <!-- Выпадающий список -->
    <div
      v-if="isDropdownOpen"
      class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-full overflow-hidden"
    >
      <button
        v-for="field in sortedFields"
        :key="field.name"
        class="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex items-center justify-between whitespace-nowrap"
        :class="{ 
          'text-primary bg-blue-50': sortedBy === field.name || (sortedBy === null && lastSelectedField === field.name)
        }"
        @click="selectField(field.name)"
      >
        {{ field.title }}
        <img
          v-if="sortedBy === field.name && orderBy !== null"
          src="@/public/assets/img/svg/ArrowUp.svg"
          alt="sort direction"
          class="transition-transform duration-300 ml-2"
          :class="{ 'rotate-180': orderBy === 'asc' }"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import IChevronDown from '../UINew/icons/IChevronDown.vue';

interface SortField {
  name: string;
  title: string;
}

interface Props {
  sortedFields: SortField[];
  sortedBy: string | null;
  orderBy: "asc" | "desc" | null;
}

interface Emits {
  (e: "sort", payload: { field: string | null; order: "asc" | "desc" | null }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isDropdownOpen = ref(false);
const lastSelectedField = ref<string | null>(null);

// Получаем название текущего выбранного поля
const currentFieldTitle = computed(() => {
  // Если есть активная сортировка, показываем её
  if (props.sortedBy) {
    const field = props.sortedFields.find(f => f.name === props.sortedBy);
    return field ? field.title : 'Просмотры';
  }
  
  // Если сортировка сброшена, но есть последнее выбранное поле, показываем его
  if (lastSelectedField.value) {
    const field = props.sortedFields.find(f => f.name === lastSelectedField.value);
    if (field) return field.title;
  }
  
  // По умолчанию показываем первое поле
  return 'Просмотры';
});

// Логика сортировки для левой кнопки (переключение направления)
const handleSortToggle = () => {
  if (props.sortedBy === null) {
    // Если ничего не выбрано, используем последнее выбранное поле или первое из списка
    const fieldToUse = lastSelectedField.value || props.sortedFields[0].name;
    lastSelectedField.value = fieldToUse;
    emit("sort", { field: fieldToUse, order: "desc" });
  } else {
    // Сохраняем текущее поле как последнее выбранное
    lastSelectedField.value = props.sortedBy;
    
    // Переключаем направление: desc -> asc -> null
    if (props.orderBy === "desc") {
      emit("sort", { field: props.sortedBy, order: "asc" });
    } else if (props.orderBy === "asc") {
      emit("sort", { field: null, order: null });
    } else {
      emit("sort", { field: props.sortedBy, order: "desc" });
    }
  }
};

// Открытие/закрытие выпадающего списка
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

// Выбор поля из выпадающего списка
const selectField = (fieldName: string) => {
  // Сохраняем выбранное поле
  lastSelectedField.value = fieldName;
  
  if (props.sortedBy !== fieldName) {
    // Если выбрали другое поле - начинаем с desc
    emit("sort", { field: fieldName, order: "desc" });
  } else {
    // Если выбрали то же поле - переключаем направление
    handleSortToggle();
  }
  isDropdownOpen.value = false;
};

// Ссылка на корневой элемент компонента
const selectRef = ref<HTMLElement | null>(null);

// Закрытие выпадающего списка при клике вне компонента
const closeDropdown = (event: Event) => {
  const target = event.target as HTMLElement;
  // Проверяем, что клик произошел вне нашего компонента селекта
  if (selectRef.value && !selectRef.value.contains(target)) {
    isDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>
